import Vue from "vue";
import VueRouter, { NavigationGuard, Route, RouteConfig } from "vue-router";
import { container } from "@/services/registerServiceProvider";
import { IAuthService } from "frontend-core";
import LoginView from "@/views/LoginView.vue";
import HomePage from "@/views/HomePage.vue";
import { IStoreService } from "@/services/IStoreService";
import { IUserRoleService } from "@/services/IUserRoleService";

Vue.use(VueRouter);

const beforeEachGuard: NavigationGuard = (to: Route, from: Route, next) => {
  const storeService: IStoreService = container.resolve<IStoreService>(
    "IStoreService",
  );
  const authService = container.resolve<IAuthService>("IAuthService");

  if (!storeService.isManualLogout) {
    storeService.currentPath = from.fullPath;
  }

  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!authService.isAuth && !to.query.lkAuthFromTablet) {
      next({
        name: "login",
        params: { nextUrl: to.fullPath },
      });
    } else {
      next();
    }
  } else if (to.matched.some(record => record.meta.guest)) {
    if (!authService.isAuth && !to.query.lkAuthFromTablet) {
      next();
    } else {
      next({ name: "HomePage" });
    }
  } else {
    next();
  }
};

const routes: RouteConfig[] = [
  {
    path: "/",
    component: HomePage,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/login",
    name: "login",
    props: true,
    component: LoginView,
    meta: {
      guest: true,
    },
  },
  {
    path: "/reset-password",
    name: "reset-password",
    props: true,
    meta: {
      guest: true,
    },
    component: () =>
      import(
        /* webpackChunkName: "reset-password" */ "@/views/ResetPasswordView.vue"
      ),
  },
  {
    path: "/agent/:id",
    name: "agent-dashboard",
    props: true,
    component: () =>
      import(
        /* webpackChunkName: "agent-dashboard" */ "@/views/AgentDashboard/AgentDashboard.vue"
      ),
    meta: {
      requiresAuth: true,
    },
    beforeEnter: async (to: Route, from: Route, next) => {
      await Promise.all([
        import("@/modules/Profile"),
        import("@/modules/Segmentation"),
      ]);

      next();
    },
  },
  {
    path: "/segmentation/search",
    name: "segmentation-search",
    props: true,
    component: () =>
      import(
        /* webpackChunkName: "segmentation-search" */ "@/views/SearchSegmentation.vue"
      ),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/segmentation",
    props: true,
    component: () =>
      import(
        /* webpackChunkName: "segmentation" */ "@/views/SegmentationTargetCmpt.vue"
      ),
    beforeEnter: async (to: Route, from: Route, next) => {
      await import("@/modules/Segmentation");
      next();
    },
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: ":agentId/editor",
        name: "segmentation-editor",
        props: route => ({
          ...route.params,
          preSelectedRuleKeyIndex: route.query.rulesIndex,
          preSelectedRuleIndex: route.query.selectedRuleIndex,
        }),
        component: () =>
          import(
            /* webpackChunkName: "segmentation-editor" */ "@/views/SegmentationEditor.vue"
          ),
      },
      {
        path: ":agentId/report",
        name: "segmentation-report",
        props: route => ({
          ...route.params,
          preSelectedRuleKeyIndex: route.query.rulesIndex,
          preSelectedRuleIndex: route.query.selectedRuleIndex,
        }),
        component: () =>
          import(
            /* webpackChunkName: "segmentation-report" */ "@/views/SegmentationReport.vue"
          ),
        beforeEnter: (to, from, next) => {
          if (
            !container.resolve<IUserRoleService>("IUserRoleService")
              .isHasChangeParentRole
          ) {
            next();
          } else {
            next({ name: "403" });
          }
        },
      },
      {
        path: ":agentId/logs",
        name: "segmentation-logs",
        props: true,
        component: () =>
          import(
            /* webpackChunkName: "segmentation-logs" */ "@/views/SegmentationLogs.vue"
          ),
      },
    ],
  },
  {
    path: "/:agentId/children/:page",
    name: "curator-children",
    props: route=> ({
      ...route.params
    }),
    component: () =>
      import(/* webpackChunkName: "curator-children" */ "@/views/AgentsList.vue"),
    beforeEnter: async (to: Route, from: Route, next) => {
      await Promise.all([
        import("@/modules/Profile"),
      ]);

      next();
    },
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/403",
    name: "403",
    props: true,
    component: () =>
      import(/* webpackChunkName: "forbidden-page" */ "@/views/403Page.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/releases",
    name: "releases",
    props: true,
    component: () =>
      import(/* webpackChunkName: "releases" */ "@/views/ChangeLog.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/help",
    name: "help",
    component: () =>
      import(/* webpackChunkName: "help" */ "@/views/HelpPage.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/agents",
    name: "agents",
    component: () =>
      import(/* webpackChunkName: "help" */ "@/views/AgentsPage.vue"),
    meta: {
      requiresAuth: true,
    },
  },
];

const router = new VueRouter({
  routes,
  mode: "history",
});

router.beforeEach(beforeEachGuard);

export default router;
